import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  CssBaseline,
  AppBar,
  Toolbar,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  MenuItem,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import EditIcon from "@material-ui/icons/Edit";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import GavelIcon from "@material-ui/icons/Gavel";
import { useNavigate } from "react-router-dom";
import {
  createWniosek,
  addCommentWithNotification,
  getExistingMRNs,
  fetchUsers,
  fetchPowodWpisaniaOptions,
} from "./RejestrFunctions"; // Import funkcji API
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment-timezone";
import { jwtDecode } from "jwt-decode";

const UNIQUE_NUMBER_INITIALS_MRN_REASONS = new Set([
  "PISMO DO URZĘDU",
  "PISMO DO KONTRAHENTA",
]);

const buildUniqueNumber = ({ procedura, mrn, fullName, powodWpisania }) => {
  const trimmedName = (fullName || "").trim();
  const firstInitial = trimmedName[1] ? trimmedName[1].toUpperCase() : "";
  const secondInitial = trimmedName[0] ? trimmedName[0].toUpperCase() : "";
  const initials = `${firstInitial}${secondInitial}`;
  const normalizedReason = (powodWpisania || "").trim().toUpperCase();

  if (UNIQUE_NUMBER_INITIALS_MRN_REASONS.has(normalizedReason)) {
    return `${mrn}.${initials}`;
  }

  return `${procedura}.${mrn}.${initials}`;
};

const splitMrnValues = (rawValue) =>
  String(rawValue || "")
    .split(/[,;\n]+/)
    .map((value) => value.toUpperCase().replace(/\s+/g, ""))
    .filter(Boolean);

const normalizeMrnList = (rawValue) => splitMrnValues(rawValue).join(", ");

const createExistingMrnTokenSet = (existingMrns = []) => {
  const tokenSet = new Set();
  existingMrns.forEach((mrnValue) => {
    splitMrnValues(mrnValue).forEach((token) => tokenSet.add(token));
  });
  return tokenSet;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop: theme.spacing(10), // Dodaj margines od góry
  },
  paper: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    maxWidth: "1500px", // Jeszcze szersze
    margin: "0 auto", // Wyśrodkowanie
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
  },
  header: {
    margin: theme.spacing(4, 0, 2),
    textAlign: "center",
  },
  textField: {
    width: "100%", // Ustaw szerokość na 100% szerokości kontenera
    "& .MuiOutlinedInput-root": {
      height: "auto", // Automatyczna wysokość
    },
  },
  modeSelection: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(4),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  modeCard: {
    width: 280,
    height: 200,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "scale(1.05)",
      boxShadow: theme.shadows[8],
    },
  },
  modeIcon: {
    fontSize: 60,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  modeTitle: {
    fontWeight: "bold",
  },
  backButton: {
    alignSelf: "flex-start",
    marginBottom: theme.spacing(2),
  },
  formGrid: {
    marginTop: theme.spacing(2),
  },
}));

const NowyWniosek = () => {
  const classes = useStyles();
  const mrnRegex = /^(?:[2-9][0-9])PL[A-Z0-9]{14,16}$/;
  const mrnValidationMessage = `Pole MRN musi zaczynać się od roku (20PL i wyżej), zawierać od 18 do 20 znaków alfanumerycznych (bez spacji i znaków specjalnych).`;
  const [wniosek, setWniosek] = useState("");
  const [existingMRNs, setExistingMRNs] = useState([]);
  const [users, setUsers] = useState([]);
  const [powodWpisaniaOptions, setPowodWpisaniaOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mrnError, setMrnError] = useState("");
  const [entryMode, setEntryMode] = useState(null); // null, 'excel', 'manual', 'wezwanie'
  const [manualFormData, setManualFormData] = useState({
    data_SAD: "",
    nr_MRN: "",
    nr_akt: "",
    nadawca_odbiorca: "",
    odbiorca: "",
    podpisany: "",
    rodzaj_SAD: "",
    procedura: "",
    platnik: "",
    powod_Wpisania: "",
    sciezkaDoPliku: "",
  });
  const [wezwanieFormData, setWezwanieFormData] = useState({
    znakPisma: "",
    sciezkaDoPliku: "",
    nr_MRN: "",
    nadawca_odbiorca: "",
    odbiorca: "",
    podpisany: "",
    data_odebrania: "",
    data_terminu_odpowiedzi: "",
  });
  const pageHeading =
    entryMode === "wezwanie" ? "Nowe wezwanie" : "Nowy wniosek";
  const navigate = useNavigate();

  useEffect(() => {
    const fetchExistingMRNs = async () => {
      try {
        const mrns = await getExistingMRNs();
        setExistingMRNs(mrns); // Ustaw istniejące MRN w stanie
      } catch (error) {
        console.err(error);
        toast.error("Błąd podczas pobierania numerów MRN.");
      }
    };

    const loadUsers = async () => {
      try {
        const data = await fetchUsers();

        // Sortowanie alfabetyczne po pobraniu danych
        const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
        setUsers(sortedData);
      } catch (error) {
        toast.error("Błąd podczas pobierania użytkowników.");
      }
    };

    const loadPowodWpisania = async () => {
      try {
        const data = await fetchPowodWpisaniaOptions();
        const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
        setPowodWpisaniaOptions(sortedData);
      } catch (error) {
        toast.error("Błąd podczas pobierania powodów wpisania.");
      }
    };

    fetchExistingMRNs();
    loadUsers();
    loadPowodWpisania();
  }, []);

  const getUsername = async () => {
    const token = localStorage.getItem("token");
    const decoded = jwtDecode(token);
    return decoded.username;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Sprawdź czy już jest w trakcie wysyłania
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    const username = await getUsername();

    const wnioskiArray = wniosek.split("\n").filter(Boolean); // Przekształcenie wniosku w tablicę
    const existingMRNsSet = new Set(existingMRNs); // Zestaw istniejących MRN dla szybszej walidacji

    const valuesToInsert = []; // Tablica do przechowywania wniosków do dodania
    const duplicates = []; // Tablica do przechowywania MRN duplikatów

    // Sprawdzenie, czy któryś z MRN już istnieje
    for (const wniosek of wnioskiArray) {
      const wniosekValues = wniosek.split("\t");
      const mrn = wniosekValues[1]; // Zakładam, że MRN jest na pozycji 1
      const isDuplicate = existingMRNsSet.has(mrn); // Ustal, czy jest duplikat

      if (isDuplicate) {
        duplicates.push(mrn); // Dodaj MRN do tablicy duplikatów
      }

      // Generowanie unique_number
      const fullName = wniosekValues[5]; // "Wojtaś Milena"
      const platnik = wniosekValues[8] ? wniosekValues[8].toUpperCase() : ""; // Bezpieczne przypisanie
      const powod_Wpisania = wniosekValues[9]
        ? wniosekValues[9].toUpperCase()
        : ""; // Bezpieczne przypisanie

      const uniqueNumber = buildUniqueNumber({
        procedura: wniosekValues[7],
        mrn,
        fullName,
        powodWpisania: powod_Wpisania,
      });
      const sciezkaDoPliku = wniosekValues[10] ? wniosekValues[10] : ""; // Bezpieczne przypisanie

      // Przygotowanie danych do zapisania
      valuesToInsert.push([
        wniosekValues[0], // data_SAD
        mrn, // nr_MRN
        "", // znak_pisma
        wniosekValues[2], // nr_akt
        wniosekValues[3], // nadawca_odbiorca
        wniosekValues[4], // odbiorca
        fullName, // podpisany
        wniosekValues[6], // rodzaj_SAD
        wniosekValues[7], // procedura
        platnik, // platnik (wielkie litery)
        powod_Wpisania, // powod_Wpisania (wielkie litery)
        sciezkaDoPliku,
        moment.tz("Europe/Warsaw").format("YYYY-MM-DD HH:mm:ss"), // data_wpisania
        uniqueNumber, // unique_number
        "W toku", // status
        isDuplicate ? 1 : 0, // duplikat
        moment.tz("Europe/Warsaw").format("YYYY-MM-DD HH:mm:ss"), // createdAt
        username,
      ]);
    }

    // Zapisanie wniosków do bazy
    try {
      const result = await createWniosek(valuesToInsert);

      // Dodanie komentarzy do każdego utworzonego wniosku
      const timestamp = moment
        .tz("Europe/Warsaw")
        .format("YYYY-MM-DDTHH:mm:ss");

      if (result.createdWnioski) {
        for (const wniosekData of result.createdWnioski) {
          try {
            const commentData = {
              id: wniosekData.id,
              comment: "Przypisano nowy wniosek",
              username: username,
              recipients: [wniosekData.originalFullName], // Zmienione na tablicę
              timestamp: timestamp,
              uniqueNumber: wniosekData.unique_number,
              status: "W toku",
            };

            await addCommentWithNotification(commentData);
          } catch (commentError) {
            console.error(
              `Błąd przy dodawaniu komentarza dla wniosku ${wniosekData.unique_number}:`,
              commentError,
            );
            // Kontynuuj mimo błędu komentarza
          }
        }
      }

      if (duplicates.length > 0) {
        toast.warning(
          `Uwaga! Następujące numery MRN są duplikatami: ${duplicates.join(
            ", ",
          )}`,
          { autoClose: 5000 }, // Czas trwania powiadomienia
        );
      }

      toast.success("Wnioski zapisane pomyślnie!", { autoClose: 5000 }); // Czas trwania powiadomienia
      setTimeout(() => {
        navigate("/rejestrWnioskow"); // Przekierowanie po utworzeniu wniosku
      }, 5000);
    } catch (error) {
      console.error(error);
      toast.error("Błąd podczas tworzenia wniosku.", { autoClose: 5000 }); // Czas trwania powiadomienia
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      const cursorPosition = e.target.selectionStart;
      const newValue = `${wniosek.substring(
        0,
        cursorPosition,
      )}\t${wniosek.substring(cursorPosition)}`;
      setWniosek(newValue);
      setTimeout(
        () =>
          e.target.setSelectionRange(cursorPosition + 1, cursorPosition + 1),
        0,
      );
    }
  };

  const handleManualFieldChange = (field) => (event) => {
    const rawValue = event.target.value;
    const value =
      field === "nr_MRN"
        ? rawValue.toUpperCase().replace(/\s+/g, "")
        : rawValue;

    if (field === "nr_MRN") {
      if (value && !mrnRegex.test(value)) {
        setMrnError(mrnValidationMessage);
      } else {
        setMrnError("");
      }
    }

    setManualFormData({
      ...manualFormData,
      [field]: value,
    });
  };

  const handleManualSubmit = async (event) => {
    event.preventDefault();

    // Sprawdź czy już jest w trakcie wysyłania
    if (isSubmitting) {
      return;
    }

    // Walidacja wymaganych pól
    const requiredFields = [
      "data_SAD",
      "nr_MRN",
      "nr_akt",
      "nadawca_odbiorca",
      "odbiorca",
      "podpisany",
      "rodzaj_SAD",
      "procedura",
    ];
    const missingFields = requiredFields.filter(
      (field) => !manualFormData[field],
    );
    if (missingFields.length > 0) {
      toast.error("Proszę wypełnić wszystkie wymagane pola.");
      return;
    }

    if (!mrnRegex.test(manualFormData.nr_MRN)) {
      toast.error(mrnValidationMessage);
      return;
    }

    setIsSubmitting(true);

    const username = await getUsername();
    const existingMRNsSet = new Set(existingMRNs);

    const mrn = manualFormData.nr_MRN;
    const isDuplicate = existingMRNsSet.has(mrn);

    const fullName = manualFormData.podpisany;
    const platnik = manualFormData.platnik
      ? manualFormData.platnik.toUpperCase()
      : "";
    const powod_Wpisania = manualFormData.powod_Wpisania
      ? manualFormData.powod_Wpisania.toUpperCase()
      : "";
    const uniqueNumber = buildUniqueNumber({
      procedura: manualFormData.procedura,
      mrn,
      fullName,
      powodWpisania: powod_Wpisania,
    });
    const sciezkaDoPliku = manualFormData.sciezkaDoPliku || "";

    const valuesToInsert = [
      [
        manualFormData.data_SAD,
        mrn,
        "",
        manualFormData.nr_akt,
        manualFormData.nadawca_odbiorca,
        manualFormData.odbiorca,
        fullName,
        manualFormData.rodzaj_SAD,
        manualFormData.procedura,
        platnik,
        powod_Wpisania,
        sciezkaDoPliku,
        moment.tz("Europe/Warsaw").format("YYYY-MM-DD HH:mm:ss"),
        uniqueNumber,
        "W toku",
        isDuplicate ? 1 : 0,
        moment.tz("Europe/Warsaw").format("YYYY-MM-DD HH:mm:ss"),
        username,
      ],
    ];

    try {
      const result = await createWniosek(valuesToInsert);

      const timestamp = moment
        .tz("Europe/Warsaw")
        .format("YYYY-MM-DDTHH:mm:ss");

      if (result.createdWnioski) {
        for (const wniosekData of result.createdWnioski) {
          try {
            const commentData = {
              id: wniosekData.id,
              comment: "Przypisano nowy wniosek",
              username: username,
              recipients: [wniosekData.originalFullName],
              timestamp: timestamp,
              uniqueNumber: wniosekData.unique_number,
              status: "W toku",
            };

            await addCommentWithNotification(commentData);
          } catch (commentError) {
            console.error(
              `Błąd przy dodawaniu komentarza dla wniosku ${wniosekData.unique_number}:`,
              commentError,
            );
          }
        }
      }

      if (isDuplicate) {
        toast.warning(`Uwaga! Numer MRN ${mrn} jest duplikatem.`, {
          autoClose: 5000,
        });
      }

      toast.success("Wniosek zapisany pomyślnie!", { autoClose: 5000 });
      setTimeout(() => {
        navigate("/rejestrWnioskow");
      }, 5000);
    } catch (error) {
      console.error(error);
      toast.error("Błąd podczas tworzenia wniosku.", { autoClose: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWezwanieFieldChange = (field) => (event) => {
    const rawValue = event.target.value;
    const value = field === "nr_MRN" ? normalizeMrnList(rawValue) : rawValue;

    if (field === "nr_MRN") {
      const invalidMrnValues = splitMrnValues(value).filter(
        (mrn) => !mrnRegex.test(mrn),
      );
      if (invalidMrnValues.length > 0) {
        setMrnError(
          `${mrnValidationMessage} Niepoprawne wartości: ${invalidMrnValues.join(", ")}`,
        );
      } else {
        setMrnError("");
      }
    }

    setWezwanieFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleWezwanieSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    const requiredFields = [
      "znakPisma",
      "sciezkaDoPliku",
      "nr_MRN",
      "podpisany",
    ];

    const missingFields = requiredFields.filter(
      (field) => !String(wezwanieFormData[field] || "").trim(),
    );

    if (missingFields.length > 0) {
      toast.error("Proszę wypełnić wszystkie wymagane pola wezwania.");
      return;
    }

    const mrnValues = splitMrnValues(wezwanieFormData.nr_MRN);
    if (mrnValues.length === 0) {
      toast.error("Proszę uzupełnić numer MRN.");
      return;
    }

    const invalidMrnValues = mrnValues.filter((mrn) => !mrnRegex.test(mrn));
    if (invalidMrnValues.length > 0) {
      toast.error(mrnValidationMessage);
      return;
    }

    setIsSubmitting(true);

    const username = await getUsername();
    const existingMRNsSet = createExistingMrnTokenSet(existingMRNs);
    const mrn = mrnValues.join(", ");
    const duplicateMrnValues = mrnValues.filter((mrn) =>
      existingMRNsSet.has(mrn),
    );
    const isDuplicate = duplicateMrnValues.length > 0;
    const powodWpisania = "WEZWANIE";
    const createdAt = moment.tz("Europe/Warsaw").format("YYYY-MM-DD HH:mm:ss");
    const uniqueNumber = String(wezwanieFormData.znakPisma || "").trim();

    const valuesToInsert = [
      [
        null,
        mrn,
        wezwanieFormData.znakPisma,
        "",
        wezwanieFormData.nadawca_odbiorca,
        wezwanieFormData.odbiorca,
        wezwanieFormData.podpisany,
        "",
        "WEZWANIE",
        "",
        powodWpisania,
        wezwanieFormData.sciezkaDoPliku,
        createdAt,
        uniqueNumber,
        "Odebrany z urzędu",
        isDuplicate ? 1 : 0,
        createdAt,
        username,
        wezwanieFormData.data_odebrania || null,
        wezwanieFormData.data_terminu_odpowiedzi || null,
      ],
    ];

    try {
      const result = await createWniosek(valuesToInsert);

      const timestamp = moment
        .tz("Europe/Warsaw")
        .format("YYYY-MM-DDTHH:mm:ss");

      if (result.createdWnioski) {
        for (const wniosekData of result.createdWnioski) {
          try {
            const commentData = {
              id: wniosekData.id,
              comment: "Przypisano nowe wezwanie",
              username: username,
              recipients: [wniosekData.originalFullName],
              timestamp: timestamp,
              uniqueNumber: wniosekData.unique_number,
              status: "Odebrany z urzędu",
            };

            await addCommentWithNotification(commentData);
          } catch (commentError) {
            console.error(
              `Błąd przy dodawaniu komentarza dla wezwania ${wniosekData.unique_number}:`,
              commentError,
            );
          }
        }
      }

      if (isDuplicate) {
        toast.warning(
          `Uwaga! Następujące numery MRN są duplikatami: ${duplicateMrnValues.join(", ")}`,
          {
            autoClose: 5000,
          },
        );
      }

      if (mrnValues.length > 1) {
        toast.info(
          `Numery MRN zapisano jako listę rozdzieloną przecinkami: ${mrn}`,
          {
            autoClose: 5000,
          },
        );
      }

      toast.success("Wezwanie zapisane pomyślnie!", { autoClose: 5000 });
      setTimeout(() => {
        navigate("/rejestrWnioskow");
      }, 5000);
    } catch (error) {
      console.error(error);
      toast.error("Błąd podczas tworzenia wezwania.", { autoClose: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToSelection = () => {
    setEntryMode(null);
    setWniosek("");
    setManualFormData({
      data_SAD: "",
      nr_MRN: "",
      nr_akt: "",
      nadawca_odbiorca: "",
      odbiorca: "",
      podpisany: "",
      rodzaj_SAD: "",
      procedura: "",
      platnik: "",
      powod_Wpisania: "",
      sciezkaDoPliku: "",
    });
    setWezwanieFormData({
      znakPisma: "",
      sciezkaDoPliku: "",
      nr_MRN: "",
      nadawca_odbiorca: "",
      odbiorca: "",
      podpisany: "",
      data_odebrania: "",
      data_terminu_odpowiedzi: "",
    });
    setMrnError("");
  };

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar>
          <Typography variant="h6" noWrap>
            Nowy Wniosek
          </Typography>
        </Toolbar>
      </AppBar>
      <Container className={classes.container}>
        <Paper className={classes.paper}>
          <Typography component="h1" variant="h5" className={classes.header}>
            {pageHeading}
          </Typography>

          {/* Mode Selection */}
          {entryMode === null && (
            <>
              <Typography
                component="p"
                variant="body1"
                style={{ marginBottom: 16, textAlign: "center" }}
              >
                Wybierz sposób wprowadzania danych:
              </Typography>
              <div className={classes.modeSelection}>
                <Card
                  className={classes.modeCard}
                  onClick={() => setEntryMode("manual")}
                >
                  <CardActionArea
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <CardContent style={{ textAlign: "center" }}>
                      <EditIcon className={classes.modeIcon} />
                      <Typography variant="h6" className={classes.modeTitle}>
                        Wprowadź ręcznie
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Wypełnij formularz z polami
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
                <Card
                  className={classes.modeCard}
                  onClick={() => setEntryMode("excel")}
                >
                  <CardActionArea
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <CardContent style={{ textAlign: "center" }}>
                      <FileCopyIcon className={classes.modeIcon} />
                      <Typography variant="h6" className={classes.modeTitle}>
                        Kopiuj z Excela
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Wklej dane oddzielone tabulatorem
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
                <Card
                  className={classes.modeCard}
                  onClick={() => setEntryMode("wezwanie")}
                >
                  <CardActionArea
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <CardContent style={{ textAlign: "center" }}>
                      <GavelIcon className={classes.modeIcon} />
                      <Typography variant="h6" className={classes.modeTitle}>
                        Wezwanie
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Wypełnij formularz wezwania - dodaj tylko jeśli wezwanie
                        nie dotyczy wysłanych przez nas wniosków
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </div>
            </>
          )}

          {/* Excel Mode */}
          {entryMode === "excel" && (
            <>
              <IconButton
                onClick={handleBackToSelection}
                className={classes.backButton}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography
                component="p"
                variant="body1"
                className={classes.instructions}
              >
                Aby złożyć nowy wniosek, wprowadź szczegóły w poniższym polu
                tekstowym. Możesz wpisać wartości rozdzielając tabulatorem
                (przyciskt "tab" na klawiaturze). <br /> Dane powinny być w
                kolejności:
                <br />- Data SAD* (format. DD-MM-YYYY), <br />- Numer MRN*,{" "}
                <br />- Nr. akt*, <br />- Nadawca*, <br />- Odbiorca*, <br />-
                Podpisany* (Nazwa użytkownika np. plomza), <br />- Rodzaj SAD*,{" "}
                <br />- Procedura*, <br />- Płatnik, <br />- Powód wpisania,{" "}
                <br />- Ścieżka do pliku
                <br />
                <br />* - Pola wymagane
                <br />
                Jeśli chcemy dodać wiele wniosków na raz jest to możliwe poprzez
                oddzielenie danych Enterem, czyli wpisujemy wszystkie dane do
                jednego wniosku następnie przycisk enterm i wprowadzamy dane dla
                drugiego wniosku.
              </Typography>
              <form className={classes.form} noValidate onSubmit={handleSubmit}>
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  id="wniosek"
                  label="Wprowadź dane wniosku"
                  name="wniosek"
                  autoComplete="wniosek"
                  autoFocus
                  value={wniosek}
                  onChange={(e) => setWniosek(e.target.value)}
                  multiline
                  rows={15}
                  className={classes.textField}
                  onKeyDown={handleKeyDown}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={isSubmitting}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {isSubmitting ? "Tworzenie wniosków..." : "Utwórz"}
                </Button>
              </form>
            </>
          )}

          {/* Manual Mode */}
          {entryMode === "manual" && (
            <>
              <IconButton
                onClick={handleBackToSelection}
                className={classes.backButton}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography
                component="p"
                variant="body1"
                style={{ marginBottom: 16 }}
              >
                Wypełnij poniższy formularz. Pola oznaczone * są wymagane.
              </Typography>
              <form
                className={classes.form}
                noValidate
                onSubmit={handleManualSubmit}
              >
                <Grid container spacing={2} className={classes.formGrid}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      id="data_SAD"
                      label="Data SAD (DD-MM-YYYY)"
                      name="data_SAD"
                      type="date"
                      value={manualFormData.data_SAD}
                      onChange={handleManualFieldChange("data_SAD")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      id="nr_MRN"
                      label="Numer MRN"
                      name="nr_MRN"
                      value={manualFormData.nr_MRN}
                      onChange={handleManualFieldChange("nr_MRN")}
                      error={Boolean(mrnError)}
                      helperText={
                        mrnError ||
                        `Format: 20PL i wyżej + 14-16 znaków alfanumerycznych`
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      id="nr_akt"
                      label="Nr. akt"
                      name="nr_akt"
                      value={manualFormData.nr_akt}
                      onChange={handleManualFieldChange("nr_akt")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      id="nadawca_odbiorca"
                      label="Nadawca"
                      name="nadawca_odbiorca"
                      value={manualFormData.nadawca_odbiorca}
                      onChange={handleManualFieldChange("nadawca_odbiorca")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      id="odbiorca"
                      label="Odbiorca"
                      name="odbiorca"
                      value={manualFormData.odbiorca}
                      onChange={handleManualFieldChange("odbiorca")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      select
                      id="podpisany"
                      label="Podpisany (nazwa użytkownika)"
                      name="podpisany"
                      value={manualFormData.podpisany}
                      onChange={handleManualFieldChange("podpisany")}
                    >
                      {users.map((user) => (
                        <MenuItem key={user.username} value={user.name}>
                          {user.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      id="rodzaj_SAD"
                      label="Rodzaj SAD"
                      name="rodzaj_SAD"
                      value={manualFormData.rodzaj_SAD}
                      onChange={handleManualFieldChange("rodzaj_SAD")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      id="procedura"
                      label="Procedura"
                      name="procedura"
                      value={manualFormData.procedura}
                      onChange={handleManualFieldChange("procedura")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      select
                      id="platnik"
                      label="Płatnik"
                      name="platnik"
                      value={manualFormData.platnik}
                      onChange={handleManualFieldChange("platnik")}
                    >
                      <MenuItem value="KLIENT">KLIENT</MenuItem>
                      <MenuItem value="WEGA">WEGA</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      select
                      id="powod_Wpisania"
                      label="Powód wpisania"
                      name="powod_Wpisania"
                      value={manualFormData.powod_Wpisania}
                      onChange={handleManualFieldChange("powod_Wpisania")}
                    >
                      {powodWpisaniaOptions.map((option) => (
                        <MenuItem key={option.id} value={option.name}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      id="sciezkaDoPliku"
                      label="Ścieżka do pliku"
                      name="sciezkaDoPliku"
                      value={manualFormData.sciezkaDoPliku}
                      onChange={handleManualFieldChange("sciezkaDoPliku")}
                    />
                  </Grid>
                </Grid>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={isSubmitting}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {isSubmitting ? "Tworzenie wniosku..." : "Utwórz"}
                </Button>
              </form>
            </>
          )}

          {entryMode === "wezwanie" && (
            <>
              <IconButton
                onClick={handleBackToSelection}
                className={classes.backButton}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography
                component="p"
                variant="body1"
                style={{ marginBottom: 16 }}
              >
                Wypełnij formularz wezwania. Pola oznaczone * są wymagane.
              </Typography>
              <form
                className={classes.form}
                noValidate
                onSubmit={handleWezwanieSubmit}
              >
                <Grid container spacing={2} className={classes.formGrid}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      id="znakPisma"
                      label="Numer wezwania"
                      name="znakPisma"
                      value={wezwanieFormData.znakPisma}
                      onChange={handleWezwanieFieldChange("znakPisma")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      id="sciezkaDoPliku"
                      label="Ścieżka do pliku"
                      name="sciezkaDoPliku"
                      value={wezwanieFormData.sciezkaDoPliku}
                      onChange={handleWezwanieFieldChange("sciezkaDoPliku")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      id="wezwanie_nr_MRN"
                      label="Nr ewidencyjny MRN (wiele: oddziel przecinkami)"
                      name="wezwanie_nr_MRN"
                      value={wezwanieFormData.nr_MRN}
                      onChange={handleWezwanieFieldChange("nr_MRN")}
                      error={Boolean(mrnError)}
                      helperText={
                        mrnError ||
                        `Format każdej wartości: 20PL i wyżej + 14-16 znaków alfanumerycznych`
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      id="wezwanie_nadawca"
                      label="Nadawca (np. Urząd/Oddział celny)"
                      name="wezwanie_nadawca"
                      value={wezwanieFormData.nadawca_odbiorca}
                      onChange={handleWezwanieFieldChange("nadawca_odbiorca")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      id="wezwanie_odbiorca"
                      label="Adresat (np. WEGA/Importer/Eksporter)"
                      name="wezwanie_odbiorca"
                      value={wezwanieFormData.odbiorca}
                      onChange={handleWezwanieFieldChange("odbiorca")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      select
                      id="wezwanie_podpisany"
                      label="Osoba odpowiedzialna"
                      name="wezwanie_podpisany"
                      value={wezwanieFormData.podpisany}
                      onChange={handleWezwanieFieldChange("podpisany")}
                    >
                      {users.map((user) => (
                        <MenuItem key={user.username} value={user.name}>
                          {user.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      id="wezwanie_data_odebrania"
                      label="Data odebrania"
                      name="wezwanie_data_odebrania"
                      type="date"
                      value={wezwanieFormData.data_odebrania}
                      onChange={handleWezwanieFieldChange("data_odebrania")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      id="wezwanie_data_terminu_odpowiedzi"
                      label="Termin odpowiedzi na wezwanie"
                      name="wezwanie_data_terminu_odpowiedzi"
                      type="date"
                      value={wezwanieFormData.data_terminu_odpowiedzi}
                      onChange={handleWezwanieFieldChange(
                        "data_terminu_odpowiedzi",
                      )}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={isSubmitting}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {isSubmitting ? "Tworzenie wezwania..." : "Utwórz wezwanie"}
                </Button>
              </form>
            </>
          )}
        </Paper>
      </Container>
      <ToastContainer position="top-right" autoClose={1500} />
    </div>
  );
};

export default NowyWniosek;
